import { InvalidDigitCountError, InvalidStateCodeError } from "./errors";
import {
  statesWith10Digits,
  statesWith11Digits,
  stateToPrefix,
} from "./stateMapping";
import type { StateCode } from "./types";

const allStates = new Set<string>([
  ...statesWith10Digits,
  ...statesWith11Digits,
]);

interface Tokens {
  ff?: string;
  fff?: string;
  bbb?: string;
  bbbb?: string;
  uuu?: string;
  uuuu?: string;
  p: string;
}

function parse(steuernummer: string, state: StateCode): Tokens {
  if (statesWith10Digits.has(state) && steuernummer.length !== 10) {
    throw new InvalidDigitCountError(state, 10);
  }
  if (statesWith11Digits.has(state) && steuernummer.length !== 11) {
    throw new InvalidDigitCountError(state, 11);
  }

  switch (state) {
    case "DE-BW":
    case "DE-BE":
    case "DE-HB":
    case "DE-HH":
    case "DE-NI":
    case "DE-RP":
    case "DE-SH": {
      // Format: FF/BBB/UUUUP (10 digits)
      const match = steuernummer.match(
        /(?<ff>\d{2})(?<bbb>\d{3})(?<uuuu>\d{4})(?<p>\d{1})/
      );
      return match?.groups as unknown as Tokens;
    }
    case "DE-BY":
    case "DE-BB":
    case "DE-MV":
    case "DE-SL":
    case "DE-SN":
    case "DE-ST":
    case "DE-TH": {
      // Format: FFF/BBB/UUUUP (11 digits)
      const match = steuernummer.match(
        /(?<fff>\d{3})(?<bbb>\d{3})(?<uuuu>\d{4})(?<p>\d{1})/
      );
      return match?.groups as unknown as Tokens;
    }
    case "DE-HE": {
      // Format: 0FF/BBB/UUUUP (11 digits, leading 0)
      const match = steuernummer.match(
        /0(?<ff>\d{2})(?<bbb>\d{3})(?<uuuu>\d{4})(?<p>\d{1})/
      );
      return match?.groups as unknown as Tokens;
    }
    case "DE-NW": {
      // Format: FFF/BBBB/UUUP (11 digits, 4-digit Bezirksnummer)
      const match = steuernummer.match(
        /(?<fff>\d{3})(?<bbbb>\d{4})(?<uuu>\d{3})(?<p>\d{1})/
      );
      return match?.groups as unknown as Tokens;
    }
    default:
      throw new InvalidStateCodeError(state);
  }
}

function compile(tokens: Tokens, state: StateCode): string {
  const { ff, fff, bbb, bbbb, uuu, uuuu, p } = tokens;
  const prefix = stateToPrefix[state];

  if (state === "DE-NW") {
    return `${prefix}${fff}0${bbbb}${uuu}${p}`;
  }
  if (ff !== undefined) {
    return `${prefix}${ff}0${bbb}${uuuu}${p}`;
  }
  return `${prefix}${fff}0${bbb}${uuuu}${p}`;
}

/** Normalizes a Steuernummer from state format (Standardschema der Länder)
 *  to the 13-digit electronic format (Vereinheitlichtes Bundesschema zur elektronischen Übermittlung) */
export function normalizeSteuernummer(
  steuernummer: string,
  state: StateCode
): string {
  if (typeof steuernummer !== "string") {
    throw new TypeError("`steuernummer` must be a string");
  }
  if (typeof state !== "string") {
    throw new TypeError("`state` must be a string");
  }
  if (!allStates.has(state)) {
    throw new InvalidStateCodeError(state);
  }

  // Remove slashes and spaces as included in official letters
  const cleaned = steuernummer.replace(/[/ ]/g, "");
  const tokens = parse(cleaned, state);
  return compile(tokens, state);
}
